import { HttpClient } from '../api/HttpClient';
import { API_ENDPOINTS } from '../api/ApiConfig';
import type {
  LoginRequest,
  LoginResponse,
  MerchantLoginResponse,
  AdminLoginResponse,
  RegisterRequest,
  CustomerRegisterResponse,
  AdminRegisterResponse,
  MerchantRegisterResponse,
  AdminRegisterRequest,
  MerchantRegisterRequest,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  UpdateProfileRequest,
  UpdateProfileResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
  VerifyPhoneRequest,
  VerifyPhoneResponse,
  OTPVerificationRequest,
  OTPVerificationResponse,
  EmailVerificationResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  GetProfileResponse,
  UserProfile,
  AuthTokens,
  AdminLoginData,
} from '../../types/api/auth';
// import type { ApiResponse } from '../../types/api/common';

/**
 * Authentication Service
 * 
 * Handles all authentication-related operations:
 * - User login/logout
 * - User registration
 * - Password management
 * - Profile management
 * - Token management
 * - Email/Phone verification
 */
export class AuthService {
  private httpClient: HttpClient;
  private currentUser: UserProfile | null = null;
  private authTokens: AuthTokens | null = null;
  private refreshPromise: Promise<AuthTokens> | null = null;

  constructor() {
    this.httpClient = HttpClient.getInstance();
    this.loadStoredAuth();
  }

  /**
   * Load stored authentication data on service initialization
   */
  private loadStoredAuth(): void {
    try {
      const storedUser = this.getStoredData('user');
      const storedTokens = this.getStoredData('tokens');

      if (storedUser && storedTokens) {
        this.currentUser = JSON.parse(storedUser);
        this.authTokens = JSON.parse(storedTokens);
        
        // Validate token expiry
        if (this.isTokenExpired()) {
          this.clearStoredAuth();
        }
      }
    } catch (error) {
      console.warn('Failed to load stored authentication data:', error);
      this.clearStoredAuth();
    }
  }

  /**
   * Get stored data from session/local storage
   */
  private getStoredData(key: string): string | null {
    try {
      return sessionStorage.getItem(key) || localStorage.getItem(key);
    } catch (error) {
      console.warn(`Failed to retrieve ${key} from storage:`, error);
      return null;
    }
  }

  /**
   * Store authentication data
   */
  private storeAuthData(user: UserProfile, tokens: AuthTokens, rememberMe: boolean = false): void {
    try {
      const storage = rememberMe ? localStorage : sessionStorage;
      
      storage.setItem('user', JSON.stringify(user));
      storage.setItem('tokens', JSON.stringify(tokens));
      storage.setItem('rememberMe', rememberMe.toString());
      
      this.currentUser = user;
      this.authTokens = tokens;
    } catch (error) {
      console.error('Failed to store authentication data:', error);
    }
  }

  /**
   * Clear stored authentication data
   */
  private clearStoredAuth(): void {
    try {
      // Clear from both storages
      ['user', 'tokens', 'rememberMe'].forEach(key => {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
      });
      
      this.currentUser = null;
      this.authTokens = null;
      this.refreshPromise = null;
    } catch (error) {
      console.error('Failed to clear authentication data:', error);
    }
  }

  /**
   * Check if current token is expired
   */
  private isTokenExpired(): boolean {
    if (!this.authTokens) return true;

    const parts = this.authTokens.accessToken.split('.');
    // Mock / opaque tokens (e.g. local dev) are valid until explicit logout
    if (parts.length !== 3) {
      return false;
    }

    try {
      const payload = JSON.parse(atob(parts[1]));
      const expiry = payload.exp * 1000;
      const now = Date.now();
      const buffer = 5 * 60 * 1000;

      return now >= (expiry - buffer);
    } catch (error) {
      console.warn('Failed to parse token expiry:', error);
      return false;
    }
  }

  /**
   * Login customer user
   */
  public async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await this.httpClient.post<LoginResponse>(
        API_ENDPOINTS.AUTH.LOGIN,
        credentials
      );

      // Store authentication data
      this.storeAuthData(
        response.data.data.user,
        response.data.data.tokens,
        false // No rememberMe for now
      );

      // Dispatch login event
      this.dispatchAuthEvent('login', response.data.data.user);

      return response.data;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  /**
   * Login admin user
   */
  public async loginAdmin(credentials: LoginRequest): Promise<AdminLoginResponse> {
    try {
      // Response handler returns ApiResponse<T> with T being the actual payload we need
      const response = await this.httpClient.post<AdminLoginData>(
        API_ENDPOINTS.ADMIN.LOGIN,
        credentials
      );
      console.log('Admin login response:', response);

      const payload = response.data;

      // Normalize tokens to our internal structure
      const normalizedTokens = {
        accessToken: payload.accessToken,
        refreshToken: payload.refreshToken,
        tokenType: payload.tokenType,
        expiresIn: payload.expiresIn,
      } as unknown as AuthTokens;

      const adminProfile = payload.admin as unknown as any;

      // Store authentication data
      this.storeAuthData(
        adminProfile,
        normalizedTokens,
        false // No rememberMe for now
      );

      // Dispatch login event
      this.dispatchAuthEvent('login', adminProfile);

      // Return in the legacy shape expected by callers (admin, tokens, message)
      return {
        admin: adminProfile,
        tokens: normalizedTokens,
        message: response.message,
      } as unknown as AdminLoginResponse;
    } catch (error) {
      console.error('Admin login failed:', error);
      throw error;
    }
  }

  /**
   * Login merchant user
   */
  public async loginMerchant(credentials: LoginRequest): Promise<MerchantLoginResponse> {
    try {
      const response = await this.httpClient.post<MerchantLoginResponse>(
        API_ENDPOINTS.MERCHANT.LOGIN,
        credentials
      );

      // Store authentication data
      this.storeAuthData(
        response.data.merchant,
        response.data.tokens,
        false // No rememberMe for now
      );

      // Dispatch login event
      this.dispatchAuthEvent('login', response.data.merchant);

      return response.data;
    } catch (error) {
      console.error('Merchant login failed:', error);
      throw error;
    }
  }

  /**
   * Register new customer user
   */
  public async register(userData: RegisterRequest): Promise<CustomerRegisterResponse> {
    try {
      const response = await this.httpClient.post<CustomerRegisterResponse>(
        API_ENDPOINTS.AUTH.REGISTER,
        userData
      );

      // Store authentication data
      this.storeAuthData(response.data.user, response.data.tokens);

      // Dispatch registration event
      this.dispatchAuthEvent('register', response.data.user);

      return response.data;
    } catch (error) {
      console.error('Customer registration failed:', error);
      throw error;
    }
  }

  /**
   * Register new admin user
   */
  public async registerAdmin(userData: AdminRegisterRequest): Promise<AdminRegisterResponse> {
    try {
      const response = await this.httpClient.post<AdminRegisterResponse>(
        API_ENDPOINTS.ADMIN.REGISTER,
        userData
      );

      // Store authentication data
      this.storeAuthData(response.data.admin, response.data.tokens);

      // Dispatch registration event
      this.dispatchAuthEvent('register', response.data.admin);

      return response.data;
    } catch (error) {
      console.error('Admin registration failed:', error);
      throw error;
    }
  }

  /**
   * Register new merchant user
   */
  public async registerMerchant(userData: MerchantRegisterRequest): Promise<MerchantRegisterResponse> {
    try {
      const response = await this.httpClient.post<MerchantRegisterResponse>(
        API_ENDPOINTS.MERCHANT.REGISTER,
        userData
      );

      // Store authentication data
      this.storeAuthData(response.data.merchant, response.data.tokens);

      // Dispatch registration event
      this.dispatchAuthEvent('register', response.data.merchant);

      return response.data;
    } catch (error) {
      console.error('Merchant registration failed:', error);
      throw error;
    }
  }

  /**
   * Logout customer user
   */
  public async logout(): Promise<void> {
    try {
      // Call logout endpoint if tokens are available
      if (this.authTokens) {
        await this.httpClient.post(API_ENDPOINTS.AUTH.LOGOUT, {
          refreshToken: this.authTokens.refreshToken,
        });
      }
    } catch (error) {
      console.warn('Logout API call failed:', error);
      // Continue with local logout even if API call fails
    } finally {
      // Clear local authentication data
      const user = this.currentUser;
      this.clearStoredAuth();
      
      // Dispatch logout event
      this.dispatchAuthEvent('logout', user);
    }
  }

  /**
   * Logout admin user
   */
  public async logoutAdmin(): Promise<void> {
    try {
      // Call admin logout endpoint if tokens are available
      if (this.authTokens) {
        await this.httpClient.post(API_ENDPOINTS.ADMIN.LOGOUT, {
          refreshToken: this.authTokens.refreshToken,
        });
      }
    } catch (error) {
      console.warn('Admin logout API call failed:', error);
      // Continue with local logout even if API call fails
    } finally {
      // Clear local authentication data
      const user = this.currentUser;
      this.clearStoredAuth();
      
      // Dispatch logout event
      this.dispatchAuthEvent('logout', user);
    }
  }

  /**
   * Logout merchant user
   */
  public async logoutMerchant(): Promise<void> {
    try {
      // Call merchant logout endpoint if tokens are available
      if (this.authTokens) {
        await this.httpClient.post(API_ENDPOINTS.MERCHANT.LOGOUT, {
          refreshToken: this.authTokens.refreshToken,
        });
      }
    } catch (error) {
      console.warn('Merchant logout API call failed:', error);
      // Continue with local logout even if API call fails
    } finally {
      // Clear local authentication data
      const user = this.currentUser;
      this.clearStoredAuth();
      
      // Dispatch logout event
      this.dispatchAuthEvent('logout', user);
    }
  }

  /**
   * Refresh authentication tokens
   */
  public async refreshTokens(): Promise<AuthTokens> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    if (!this.authTokens?.refreshToken) {
      throw new Error('No refresh token available');
    }

    this.refreshPromise = this.performTokenRefresh();
    
    try {
      const newTokens = await this.refreshPromise;
      return newTokens;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Perform actual token refresh
   */
  private async performTokenRefresh(): Promise<AuthTokens> {
    try {
      const refreshRequest: RefreshTokenRequest = {
        refreshToken: this.authTokens!.refreshToken,
      };

      const response = await this.httpClient.post<RefreshTokenResponse>(
        API_ENDPOINTS.AUTH.REFRESH_TOKEN,
        refreshRequest
      );

      // Update stored tokens
      const isRememberMe = localStorage.getItem('rememberMe') === 'true';
      if (this.currentUser) {
        this.storeAuthData(this.currentUser, response.data.data, isRememberMe);
      }

      return response.data.data;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, logout user
      await this.logout();
      throw error;
    }
  }

  /**
   * Get current user profile
   */
  public async getProfile(): Promise<GetProfileResponse> {
    try {
      const response = await this.httpClient.get<GetProfileResponse>(
        API_ENDPOINTS.AUTH.PROFILE
      );

      // Update stored user data
      if (this.authTokens) {
        const isRememberMe = localStorage.getItem('rememberMe') === 'true';
        this.storeAuthData(response.data.data, this.authTokens, isRememberMe);
      }

      return response.data;
    } catch (error) {
      console.error('Failed to get profile:', error);
      throw error;
    }
  }

  /**
   * Update user profile
   */
  public async updateProfile(profileData: UpdateProfileRequest): Promise<UpdateProfileResponse> {
    try {
      const response = await this.httpClient.patch<UpdateProfileResponse>(
        API_ENDPOINTS.AUTH.PROFILE,
        profileData
      );

      // Update stored user data
      if (this.authTokens) {
        const isRememberMe = localStorage.getItem('rememberMe') === 'true';
        this.storeAuthData(response.data.data, this.authTokens, isRememberMe);
      }

      // Dispatch profile update event
      this.dispatchAuthEvent('profileUpdate', response.data.data);

      return response.data;
    } catch (error) {
      console.error('Failed to update profile:', error);
      throw error;
    }
  }

  /**
   * Change user password
   */
  public async changePassword(passwordData: ChangePasswordRequest): Promise<ChangePasswordResponse> {
    try {
      const response = await this.httpClient.post<ChangePasswordResponse>(
        API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
        passwordData
      );

      // Dispatch password change event
      this.dispatchAuthEvent('passwordChange', this.currentUser || null);

      return response.data;
    } catch (error) {
      console.error('Failed to change password:', error);
      throw error;
    }
  }

  /**
   * Request password reset
   */
  public async forgotPassword(email: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
    try {
      const response = await this.httpClient.post<ForgotPasswordResponse>(
        API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
        email
      );

      return response.data;
    } catch (error) {
      console.error('Failed to request password reset:', error);
      throw error;
    }
  }

  /**
   * Reset password with token
   */
  public async resetPassword(resetData: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    try {
      const response = await this.httpClient.post<ResetPasswordResponse>(
        API_ENDPOINTS.AUTH.RESET_PASSWORD,
        resetData
      );

      return response.data;
    } catch (error) {
      console.error('Failed to reset password:', error);
      throw error;
    }
  }

  /**
   * Verify email address
   */
  public async verifyEmail(verificationData: VerifyEmailRequest): Promise<VerifyEmailResponse> {
    try {
      const response = await this.httpClient.post<VerifyEmailResponse>(
        API_ENDPOINTS.AUTH.VERIFY_EMAIL,
        verificationData
      );

      // Update user verification status if currently logged in
      if (this.currentUser && this.authTokens) {
        const updatedUser = { ...this.currentUser, isEmailVerified: true };
        const isRememberMe = localStorage.getItem('rememberMe') === 'true';
        this.storeAuthData(updatedUser, this.authTokens, isRememberMe);
      }

      return response.data;
    } catch (error) {
      console.error('Failed to verify email:', error);
      throw error;
    }
  }

  /**
   * Verify phone number
   */
  public async verifyPhone(verificationData: VerifyPhoneRequest): Promise<VerifyPhoneResponse> {
    try {
      const response = await this.httpClient.post<VerifyPhoneResponse>(
        API_ENDPOINTS.AUTH.VERIFY_PHONE,
        verificationData
      );

      // Update user verification status if currently logged in
      if (this.currentUser && this.authTokens) {
        const updatedUser = { ...this.currentUser, isPhoneVerified: true };
        const isRememberMe = localStorage.getItem('rememberMe') === 'true';
        this.storeAuthData(updatedUser, this.authTokens, isRememberMe);
      }

      return response.data;
    } catch (error) {
      console.error('Failed to verify phone:', error);
      throw error;
    }
  }

  /**
   * Verify OTP after registration
   * Note: For user-type-specific OTP verification, use verifyOTPByUserId instead
   */
  public async verifyOTP(verificationData: OTPVerificationRequest): Promise<OTPVerificationResponse> {
    try {
      const response = await this.httpClient.post<OTPVerificationResponse>(
        API_ENDPOINTS.AUTH.VERIFY_OTP,
        verificationData
      );

      // Store authentication data after successful OTP verification
      this.storeAuthData(
        response.data.data.user,
        response.data.data.tokens,
        false // Don't remember me for OTP verification
      );

      // Dispatch login event
      this.dispatchAuthEvent('login', response.data.data.user);

      return response.data;
    } catch (error) {
      console.error('Failed to verify OTP:', error);
      throw error;
    }
  }

  /**
   * Verify customer email by user ID
   */
  public async verifyCustomerEmail(userId: string): Promise<EmailVerificationResponse> {
    try {
      const response = await this.httpClient.get<EmailVerificationResponse>(
        `${API_ENDPOINTS.AUTH.VERIFY_EMAIL}/${userId}`
      );

      // Dispatch email verification event
      this.dispatchAuthEvent('emailVerified', this.currentUser);

      return response.data;
    } catch (error) {
      console.error('Failed to verify customer email:', error);
      throw error;
    }
  }

  /**
   * Verify admin email by user ID
   */
  public async verifyAdminEmail(userId: string): Promise<EmailVerificationResponse> {
    try {
      const response = await this.httpClient.get<EmailVerificationResponse>(
        `${API_ENDPOINTS.ADMIN.VERIFY_EMAIL}/${userId}`
      );

      // Dispatch email verification event
      this.dispatchAuthEvent('emailVerified', this.currentUser);

      return response.data;
    } catch (error) {
      console.error('Failed to verify admin email:', error);
      throw error;
    }
  }

  /**
   * Verify merchant email by user ID
   */
  public async verifyMerchantEmail(userId: string): Promise<EmailVerificationResponse> {
    try {
      const response = await this.httpClient.get<EmailVerificationResponse>(
        `${API_ENDPOINTS.MERCHANT.VERIFY_EMAIL}/${userId}`
      );

      // Dispatch email verification event
      this.dispatchAuthEvent('emailVerified', this.currentUser);

      return response.data;
    } catch (error) {
      console.error('Failed to verify merchant email:', error);
      throw error;
    }
  }

  /**
   * Verify email by user ID and user type (unified method)
   */
  public async verifyEmailByUserId(userId: string, userType: string): Promise<EmailVerificationResponse> {
    switch (userType.toLowerCase()) {
      case 'customer':
        return this.verifyCustomerEmail(userId);
      case 'admin':
        return this.verifyAdminEmail(userId);
      case 'merchant':
        return this.verifyMerchantEmail(userId);
      default:
        throw new Error(`Unsupported user type for email verification: ${userType}`);
    }
  }

  /**
   * Verify OTP by user ID and user type (unified method for OTP verification)
   */
  public async verifyOTPByUserId(userId: string, userType: string, otp: string): Promise<EmailVerificationResponse> {
    const otpData = { user_id: userId, verificationCode: otp } as OTPVerificationRequest;
    
    switch (userType.toLowerCase()) {
      case 'customer':
        return this.verifyCustomerOTP(otpData);
      case 'admin':
        return this.verifyAdminOTP(otpData);
      case 'merchant':
        return this.verifyMerchantOTP(otpData);
      default:
        throw new Error(`Unsupported user type for OTP verification: ${userType}`);
    }
  }

  /**
   * Verify customer OTP
   */
  private async verifyCustomerOTP(otpData: OTPVerificationRequest): Promise<EmailVerificationResponse> {
    try {
      const response = await this.httpClient.post<EmailVerificationResponse>(
        `${API_ENDPOINTS.AUTH.VERIFY_OTP}/${otpData.user_id}`,
        { verificationCode: otpData.verificationCode }
      );

      // Dispatch verification event
      this.dispatchAuthEvent('emailVerified', this.currentUser);

      return response.data;
    } catch (error) {
      console.error('Failed to verify customer OTP:', error);
      throw error;
    }
  }

  /**
   * Verify admin OTP
   */
  private async verifyAdminOTP(otpData: OTPVerificationRequest): Promise<EmailVerificationResponse> {
    try {
      const response = await this.httpClient.post<EmailVerificationResponse>(
        `${API_ENDPOINTS.ADMIN.VERIFY_EMAIL}/${otpData.user_id}`,
        { verificationCode: otpData.verificationCode }
      );

      // Dispatch verification event
      this.dispatchAuthEvent('emailVerified', this.currentUser);

      return response.data;
    } catch (error) {
      console.error('Failed to verify admin OTP:', error);
      throw error;
    }
  }

  /**
   * Verify merchant OTP
   */
  private async verifyMerchantOTP(otpData: OTPVerificationRequest): Promise<EmailVerificationResponse> {
    try {
      const response = await this.httpClient.post<EmailVerificationResponse>(
        `${API_ENDPOINTS.MERCHANT.VERIFY_EMAIL}/${otpData.user_id}`,
        { verificationCode: otpData.verificationCode }
      );

      // Dispatch verification event
      this.dispatchAuthEvent('emailVerified', this.currentUser);

      return response.data;
    } catch (error) {
      console.error('Failed to verify merchant OTP:', error);
      throw error;
    }
  }

  /**
   * Resend verification code by user type
   */
  public async resendVerificationByUserId(userId: string, userType: string): Promise<EmailVerificationResponse> {
    switch (userType.toLowerCase()) {
      case 'customer':
        return this.resendCustomerVerification(userId);
      case 'admin':
        return this.resendAdminVerification(userId);
      case 'merchant':
        return this.resendMerchantVerification(userId);
      default:
        throw new Error(`Unsupported user type for resend verification: ${userType}`);
    }
  }

  private async resendCustomerVerification(userId: string): Promise<EmailVerificationResponse> {
    try {
      const response = await this.httpClient.post<EmailVerificationResponse>(
        `${API_ENDPOINTS.AUTH.RESEND_VERIFICATION}/${userId}`,
        {}
      );
      return response.data;
    } catch (error) {
      console.error('Failed to resend customer verification:', error);
      throw error;
    }
  }

  private async resendAdminVerification(userId: string): Promise<EmailVerificationResponse> {
    try {
      const response = await this.httpClient.post<EmailVerificationResponse>(
        `${API_ENDPOINTS.ADMIN.RESEND_VERIFICATION}/${userId}`,
        {}
      );
      return response.data;
    } catch (error) {
      console.error('Failed to resend admin verification:', error);
      throw error;
    }
  }

  private async resendMerchantVerification(userId: string): Promise<EmailVerificationResponse> {
    try {
      const response = await this.httpClient.post<EmailVerificationResponse>(
        `${API_ENDPOINTS.MERCHANT.RESEND_VERIFICATION}/${userId}`,
        {}
      );
      return response.data;
    } catch (error) {
      console.error('Failed to resend merchant verification:', error);
      throw error;
    }
  }

  /**
   * Get current user
   */
  public getCurrentUser(): UserProfile | null {
    return this.currentUser;
  }

  /**
   * Get current auth tokens
   */
  public getAuthTokens(): AuthTokens | null {
    return this.authTokens;
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    return !!(this.currentUser && this.authTokens && !this.isTokenExpired());
  }

  /**
   * Get user role from profile
   */
  private getUserRole(): string | null {
    if (!this.currentUser) return null;
    
    // Check if it's a merchant profile
    if ('merchantProfileId' in this.currentUser) {
      return 'MERCHANT';
    }
    
    // Check if it's an admin profile
    if ('adminProfileId' in this.currentUser) {
      return 'ADMIN';
    }
    
    // Check if it's a regular user
    if ('role' in this.currentUser) {
      return this.currentUser.role;
    }
    
    return null;
  }

  /**
   * Get user ID from profile
   */
  private getUserId(): string | null {
    if (!this.currentUser) return null;
    
    // Check if it's a merchant profile
    if ('userId' in this.currentUser) {
      return this.currentUser.userId;
    }
    
    // Check if it's a regular user
    if ('id' in this.currentUser) {
      return this.currentUser.id;
    }
    
    return null;
  }

  /**
   * Check if user has specific role
   */
  public hasRole(role: string): boolean {
    return this.getUserRole() === role;
  }

  /**
   * Check if user has any of the specified roles
   */
  public hasAnyRole(roles: string[]): boolean {
    const userRole = this.getUserRole();
    return !!(userRole && roles.includes(userRole));
  }

  /**
   * Get access token for API requests
   */
  public getAccessToken(): string | null {
    if (!this.authTokens || this.isTokenExpired()) {
      return null;
    }
    return this.authTokens.accessToken;
  }

  /**
   * Dispatch authentication events
   */
  private dispatchAuthEvent(type: string, user: UserProfile | null): void {
    const event = new CustomEvent(`auth:${type}`, {
      detail: { user, timestamp: new Date().toISOString() }
    });
    window.dispatchEvent(event);
  }

  /**
   * Set up automatic token refresh
   */
  public setupAutoRefresh(): void {
    const checkInterval = 60000; // Check every minute
    
    const intervalId = setInterval(async () => {
      if (this.isAuthenticated() && this.isTokenExpired()) {
        try {
          await this.refreshTokens();
          console.info('Tokens refreshed automatically');
        } catch (error) {
          console.warn('Auto token refresh failed:', error);
          clearInterval(intervalId);
        }
      }
    }, checkInterval);

    // Clear interval on logout
    window.addEventListener('auth:logout', () => {
      clearInterval(intervalId);
    }, { once: true });
  }

  /**
   * Health check for service monitoring
   */
  public healthCheck(): { healthy: boolean; message: string; details?: any } {
    try {
      const hasHttpClient = !!this.httpClient;
      const canAccessStorage = this.testStorageAccess();
      
      return {
        healthy: hasHttpClient && canAccessStorage,
        message: hasHttpClient && canAccessStorage 
          ? 'AuthService is healthy' 
          : 'AuthService has issues',
        details: {
          httpClient: hasHttpClient,
          storageAccess: canAccessStorage,
          isAuthenticated: this.isAuthenticated(),
          currentUser: this.getUserId(),
        },
      };
    } catch (error) {
      return {
        healthy: false,
        message: 'AuthService health check failed',
        details: { error: error instanceof Error ? error.message : 'Unknown error' },
      };
    }
  }

  /**
   * Test storage access
   */
  private testStorageAccess(): boolean {
    try {
      const testKey = 'test_storage_access';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Dispose of service resources
   */
  public dispose(): void {
    this.clearStoredAuth();
    // Any other cleanup can be added here
  }
}
